import * as d3 from "d3";

/** Datum d'entrée : un nœud de l'arbre hiérarchique. */
export interface PieDatum {
  name: string;
  /** Valeur de la feuille. Si l'enfant a des `children`, sa valeur est la somme de leurs valeurs (calculée si absente). */
  value?: number;
  children?: PieDatum[];
}

export type PiePalette = "tableau10" | "viridis" | "category10" | string[];

export interface PieChartOptions {
  /** Largeur du SVG (px). Défaut 480. */
  width?: number;
  /** Hauteur du SVG (px). Défaut 480. */
  height?: number;
  /** Donut intérieur (px) ; 0 = pie plein. Défaut 80. */
  innerRadius?: number;
  /** Palette de couleurs pour les segments. Défaut "tableau10". */
  palette?: PiePalette;
  /** Durée des animations (ms). Défaut 350. */
  animationMs?: number;
  /** Affiche le breadcrumb (chemin du drilldown). Défaut true. */
  breadcrumbs?: boolean;
  /** Affiche la légende cliquable. Défaut true. */
  legend?: boolean;
  /** Nom des niveaux (ex. ["rayon", "famille", "sous-famille"]). Affiché dans le breadcrumb. */
  levels?: string[];
  /**
   * Callback déclenché lors d'un changement de niveau. Permet à l'appelant de
   * fetch les données du niveau suivant (drilldown lazy) et d'appeler
   * `chart.update(newData)` si besoin.
   */
  onLevelChange?: (level: number, path: string[]) => void;
}

const PALETTES: Record<Exclude<PiePalette, string[]>, readonly string[]> = {
  tableau10: d3.schemeTableau10,
  category10: d3.schemeCategory10,
  viridis: d3.range(10).map((i) => d3.interpolateViridis(i / 9)),
};

function resolvePalette(p: PiePalette | undefined): readonly string[] {
  if (Array.isArray(p)) return p;
  if (typeof p === "string" && p in PALETTES) return PALETTES[p as keyof typeof PALETTES];
  return PALETTES.tableau10;
}

/**
 * Calcule la valeur d'un nœud (récursif si children présents).
 */
function computeValue(node: PieDatum): number {
  if (Array.isArray(node.children) && node.children.length > 0) {
    return node.children.reduce((sum, c) => sum + computeValue(c), 0);
  }
  return typeof node.value === "number" && node.value > 0 ? node.value : 0;
}

interface ArcDatum {
  name: string;
  value: number;
  hasChildren: boolean;
  /** Référence vers le nœud original pour pouvoir naviguer en drilldown. */
  node: PieDatum;
}

/**
 * Composant pie chart D3 avec drilldown N niveaux.
 *
 * Usage :
 *   const chart = new PieChart(container, { levels: ["rayon", "famille", "sous-famille"] });
 *   chart.update({ name: "Total", children: [...] });
 *   // Clic sur un segment qui a des children → drilldown
 *   // Clic sur centre / breadcrumb → drillup
 *
 * Si la data est lazy-chargée par niveau, fournir `onLevelChange` qui fetch
 * les enfants puis appelle `chart.update(...)` avec la data complète.
 */
export class PieChart {
  private container: HTMLElement;
  private opts: Required<Omit<PieChartOptions, "onLevelChange" | "levels" | "palette">> & {
    onLevelChange?: PieChartOptions["onLevelChange"];
    levels: string[];
    palette: readonly string[];
  };
  private svg: SVGSVGElement | null = null;
  private breadcrumbEl: HTMLDivElement | null = null;
  private legendEl: HTMLDivElement | null = null;
  /** Data root (telle que fournie par l'appelant). */
  private root: PieDatum | null = null;
  /** Chemin courant : indices imbriqués depuis root. ex. [0, 2] = root.children[0].children[2] */
  private path: number[] = [];
  /** Filtre légende : noms cachés (au niveau courant). */
  private hiddenNames = new Set<string>();
  private colorScale: d3.ScaleOrdinal<string, string> = d3.scaleOrdinal<string, string>();

  constructor(container: HTMLElement, options: PieChartOptions = {}) {
    this.container = container;
    this.opts = {
      width: options.width ?? 480,
      height: options.height ?? 480,
      innerRadius: options.innerRadius ?? 80,
      palette: resolvePalette(options.palette),
      animationMs: options.animationMs ?? 350,
      breadcrumbs: options.breadcrumbs ?? true,
      legend: options.legend ?? true,
      levels: options.levels ?? [],
      onLevelChange: options.onLevelChange,
    };
    this.initDom();
  }

  private initDom() {
    this.container.innerHTML = "";
    this.container.style.fontFamily = this.container.style.fontFamily || "system-ui, sans-serif";

    if (this.opts.breadcrumbs) {
      const bc = document.createElement("div");
      bc.className = "viz-art-breadcrumbs";
      bc.style.cssText =
        "margin-bottom: 0.5rem; font-size: 14px; color: #374151; display: flex; flex-wrap: wrap; gap: 4px; align-items: center;";
      this.container.appendChild(bc);
      this.breadcrumbEl = bc;
    }

    const svg = d3
      .create("svg")
      .attr("width", this.opts.width)
      .attr("height", this.opts.height)
      .attr("viewBox", `0 0 ${this.opts.width} ${this.opts.height}`)
      .attr("style", "max-width: 100%; height: auto; display: block;");
    this.container.appendChild(svg.node()!);
    this.svg = svg.node();

    if (this.opts.legend) {
      const lg = document.createElement("div");
      lg.className = "viz-art-legend";
      lg.style.cssText =
        "margin-top: 0.5rem; display: flex; flex-wrap: wrap; gap: 8px 16px; font-size: 13px; color: #374151;";
      this.container.appendChild(lg);
      this.legendEl = lg;
    }
  }

  /**
   * Re-initialise le chart avec une nouvelle data root.
   * Le path et le filtre légende sont reset.
   */
  update(data: PieDatum) {
    this.root = data;
    this.path = [];
    this.hiddenNames.clear();
    this.render();
  }

  /** Retourne le nœud courant en suivant `path` depuis root. */
  private getCurrentNode(): PieDatum | null {
    if (!this.root) return null;
    let node: PieDatum = this.root;
    for (const idx of this.path) {
      if (!node.children || idx >= node.children.length) return null;
      node = node.children[idx]!;
    }
    return node;
  }

  /** Retourne le chemin (noms) du root au nœud courant. */
  private getPathNames(): string[] {
    if (!this.root) return [];
    const names: string[] = [this.root.name];
    let node: PieDatum = this.root;
    for (const idx of this.path) {
      if (!node.children || idx >= node.children.length) break;
      node = node.children[idx]!;
      names.push(node.name);
    }
    return names;
  }

  private drillTo(level: number) {
    if (level < 0) level = 0;
    this.path = this.path.slice(0, level);
    this.hiddenNames.clear();
    const names = this.getPathNames();
    this.opts.onLevelChange?.(this.path.length, names);
    this.render();
  }

  private drillInto(idx: number) {
    const current = this.getCurrentNode();
    if (!current?.children?.[idx]?.children?.length) return; // pas drillable
    this.path.push(idx);
    this.hiddenNames.clear();
    const names = this.getPathNames();
    this.opts.onLevelChange?.(this.path.length, names);
    this.render();
  }

  private render() {
    if (!this.svg) return;
    const current = this.getCurrentNode();
    const items: ArcDatum[] = (current?.children ?? [])
      .map((c) => ({
        name: c.name,
        value: computeValue(c),
        hasChildren: Array.isArray(c.children) && c.children.length > 0,
        node: c,
      }))
      .filter((d) => d.value > 0 && !this.hiddenNames.has(d.name));

    this.colorScale = d3
      .scaleOrdinal<string, string>()
      .domain((current?.children ?? []).map((c) => c.name))
      .range(this.opts.palette as string[]);

    this.renderBreadcrumbs();
    this.renderLegend(current);
    this.renderPie(items);
  }

  private renderBreadcrumbs() {
    if (!this.breadcrumbEl) return;
    this.breadcrumbEl.innerHTML = "";
    const names = this.getPathNames();
    const levels = this.opts.levels;
    names.forEach((name, idx) => {
      const isLast = idx === names.length - 1;
      const labelLevel = idx > 0 && levels[idx - 1] ? `${levels[idx - 1]}: ` : "";
      const a = document.createElement(isLast ? "span" : "a");
      a.textContent = `${labelLevel}${name}`;
      a.style.cssText = isLast
        ? "font-weight: 600; color: #111827;"
        : "cursor: pointer; color: #2563eb; text-decoration: none;";
      if (!isLast) {
        (a as HTMLAnchorElement).href = "#";
        a.addEventListener("click", (e) => {
          e.preventDefault();
          this.drillTo(idx);
        });
      }
      this.breadcrumbEl!.appendChild(a);
      if (!isLast) {
        const sep = document.createElement("span");
        sep.textContent = " › ";
        sep.style.color = "#9ca3af";
        this.breadcrumbEl!.appendChild(sep);
      }
    });
  }

  private renderLegend(current: PieDatum | null) {
    if (!this.legendEl) return;
    this.legendEl.innerHTML = "";
    const items = current?.children ?? [];
    if (items.length === 0) return;
    items.forEach((c) => {
      const isHidden = this.hiddenNames.has(c.name);
      const row = document.createElement("button");
      row.type = "button";
      row.style.cssText = `cursor: pointer; background: none; border: 0; padding: 0; display: inline-flex; align-items: center; gap: 6px; opacity: ${isHidden ? "0.4" : "1"}; color: inherit; font-size: inherit; font-family: inherit;`;
      const sw = document.createElement("span");
      sw.style.cssText = `display: inline-block; width: 12px; height: 12px; border-radius: 2px; background: ${this.colorScale(c.name)};`;
      const label = document.createElement("span");
      label.textContent = c.name;
      if (isHidden) label.style.textDecoration = "line-through";
      row.appendChild(sw);
      row.appendChild(label);
      row.addEventListener("click", () => {
        if (this.hiddenNames.has(c.name)) this.hiddenNames.delete(c.name);
        else this.hiddenNames.add(c.name);
        this.render();
      });
      this.legendEl!.appendChild(row);
    });
  }

  private renderPie(items: ArcDatum[]) {
    if (!this.svg) return;
    const { width, height, innerRadius, animationMs } = this.opts;
    const radius = Math.min(width, height) / 2;

    const svg = d3.select(this.svg);
    let g = svg.select<SVGGElement>("g.viz-art-pie");
    if (g.empty()) {
      g = svg.append("g").attr("class", "viz-art-pie");
    }
    g.attr("transform", `translate(${width / 2}, ${height / 2})`);

    if (items.length === 0) {
      g.selectAll("*").remove();
      g.append("text")
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "middle")
        .attr("fill", "#6b7280")
        .attr("font-size", 14)
        .text("Pas de données");
      return;
    }

    const pie = d3.pie<ArcDatum>().value((d) => d.value).sort(null);
    const arc = d3
      .arc<d3.PieArcDatum<ArcDatum>>()
      .innerRadius(innerRadius)
      .outerRadius(radius - 4);
    const arcs = pie(items);

    // Centre cliquable pour drillup (sauf au root)
    g.selectAll<SVGCircleElement, null>("circle.viz-art-center")
      .data(this.path.length > 0 ? [null] : [])
      .join(
        (enter) =>
          enter
            .append("circle")
            .attr("class", "viz-art-center")
            .attr("r", innerRadius - 2)
            .attr("fill", "#f3f4f6")
            .attr("stroke", "#d1d5db")
            .attr("stroke-dasharray", "2,3")
            .style("cursor", "pointer")
            .on("click", () => this.drillTo(this.path.length - 1))
            .append("title")
            .text("Retour"),
        (update) => update,
        (exit) => exit.remove()
      );

    g.selectAll<SVGTextElement, null>("text.viz-art-back")
      .data(this.path.length > 0 ? [null] : [])
      .join(
        (enter) =>
          enter
            .append("text")
            .attr("class", "viz-art-back")
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "middle")
            .attr("fill", "#6b7280")
            .attr("font-size", 14)
            .style("pointer-events", "none")
            .text("← retour"),
        (update) => update,
        (exit) => exit.remove()
      );

    const animate = animationMs > 0;

    // Segments avec transitions
    g.selectAll<SVGPathElement, d3.PieArcDatum<ArcDatum>>("path.viz-art-arc")
      .data(arcs, (d) => d.data.name)
      .join(
        (enter) =>
          enter
            .append("path")
            .attr("class", "viz-art-arc")
            .attr("fill", (d) => this.colorScale(d.data.name))
            .attr("stroke", "#fff")
            .attr("stroke-width", 1)
            .style("cursor", (d) => (d.data.hasChildren ? "pointer" : "default"))
            .on("click", (_event, d) => {
              if (!d.data.hasChildren) return;
              const current = this.getCurrentNode();
              const idx = current?.children?.findIndex((c) => c.name === d.data.name) ?? -1;
              if (idx >= 0) this.drillInto(idx);
            })
            .each(function (d) {
              (this as SVGPathElement & { _current?: d3.PieArcDatum<ArcDatum> })._current = d;
            })
            .attr("d", (d) => arc(d) ?? ""),
        (update) =>
          animate
            ? update
                .transition()
                .duration(animationMs)
                .attrTween("d", function (d) {
                  const self = this as SVGPathElement & { _current?: d3.PieArcDatum<ArcDatum> };
                  const i = d3.interpolate(self._current ?? d, d);
                  self._current = d;
                  return (t) => arc(i(t)) ?? "";
                })
                .selection()
            : update.attr("d", (d) => arc(d) ?? "").each(function (d) {
                (this as SVGPathElement & { _current?: d3.PieArcDatum<ArcDatum> })._current = d;
              }),
        (exit) => (animate ? exit.transition().duration(animationMs).style("opacity", 0).remove() : exit.remove())
      );

    // Tooltip natif (toujours, sur la sélection après join)
    const allPaths = g.selectAll<SVGPathElement, d3.PieArcDatum<ArcDatum>>("path.viz-art-arc");
    allPaths.selectAll("title").remove();
    allPaths.append("title").text((d) => `${d.data.name} : ${d.data.value}`);

    // Labels (uniquement segments > 12°)
    g.selectAll<SVGTextElement, d3.PieArcDatum<ArcDatum>>("text.viz-art-label")
      .data(
        arcs.filter((d) => d.endAngle - d.startAngle > 0.21),
        (d) => d.data.name
      )
      .join(
        (enter) =>
          enter
            .append("text")
            .attr("class", "viz-art-label")
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "middle")
            .attr("fill", "#fff")
            .attr("font-size", 12)
            .attr("font-weight", 600)
            .style("pointer-events", "none")
            .attr("transform", (d) => `translate(${arc.centroid(d)})`)
            .text((d) => d.data.name),
        (update) =>
          (animate
            ? update.transition().duration(animationMs).attr("transform", (d) => `translate(${arc.centroid(d)})`).selection()
            : update.attr("d", (d) => arc(d) ?? "").attr("transform", (d) => `translate(${arc.centroid(d)})`)
          ).text((d) => d.data.name),
        (exit) => exit.remove()
      );
  }

  destroy() {
    this.container.innerHTML = "";
    this.svg = null;
    this.breadcrumbEl = null;
    this.legendEl = null;
    this.root = null;
    this.path = [];
  }
}
